import { useQuery } from '@tanstack/react-query'
import { releaseApi } from '../api'
import type { SecretExpiryInfo } from '../api'

function daysUntil(iso: string | null): number | null {
    if (!iso) return null
    const diff = new Date(iso).getTime() - Date.now()
    return Math.ceil(diff / 86_400_000)
}

function ExpiryBadge({ secret }: { secret: SecretExpiryInfo }) {
    if (!secret.enabled) {
        return (
            <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold bg-slate-700/60 text-slate-400">
                Disabled
            </span>
        )
    }

    if (!secret.expiresOn) {
        return (
            <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold bg-slate-700/40 text-slate-500">
                No expiry
            </span>
        )
    }

    const days = daysUntil(secret.expiresOn)
    if (days === null) return null

    if (days < 0) {
        return (
            <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold bg-red-500/20 text-red-400 ring-1 ring-red-500/30">
                <span className="h-1.5 w-1.5 rounded-full bg-red-400 animate-pulse" />
                Expired {Math.abs(days)}d ago
            </span>
        )
    }
    if (days <= 7) {
        return (
            <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold bg-red-500/15 text-red-400 ring-1 ring-red-500/30">
                <span className="h-1.5 w-1.5 rounded-full bg-red-400" />
                {days}d left
            </span>
        )
    }
    if (days <= 30) {
        return (
            <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold bg-amber-500/15 text-amber-400 ring-1 ring-amber-500/30">
                <span className="h-1.5 w-1.5 rounded-full bg-amber-400" />
                {days}d left
            </span>
        )
    }
    return (
        <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold bg-emerald-500/15 text-emerald-400 ring-1 ring-emerald-500/30">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
            {days}d left
        </span>
    )
}

function formatDate(iso: string | null): string {
    if (!iso) return '—'
    return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

export function SecretsPage() {
    const { data, isLoading, isError, error, dataUpdatedAt, refetch, isFetching } = useQuery({
        queryKey: ['secret-expiry'],
        queryFn: releaseApi.getSecretExpiry,
        refetchInterval: 5 * 60_000,
        staleTime: 4 * 60_000,
    })

    const expiredCount  = data?.secrets.filter(s => s.enabled && s.expiresOn && daysUntil(s.expiresOn)! < 0).length ?? 0
    const criticalCount = data?.secrets.filter(s => s.enabled && s.expiresOn && (daysUntil(s.expiresOn) ?? 999) <= 7 && (daysUntil(s.expiresOn) ?? 999) >= 0).length ?? 0
    const warningCount  = data?.secrets.filter(s => s.enabled && s.expiresOn && (daysUntil(s.expiresOn) ?? 999) > 7 && (daysUntil(s.expiresOn) ?? 999) <= 30).length ?? 0

    return (
        <>
            <header className="border-b border-slate-800 bg-slate-900/80 backdrop-blur-sm sticky top-0 z-10">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
                    <span className="text-sm font-semibold text-slate-200">Secrets & Token Expiry</span>
                    <div className="flex items-center gap-4 text-xs text-slate-500">
                        {dataUpdatedAt > 0 && (
                            <span>Updated {new Date(dataUpdatedAt).toLocaleTimeString()}</span>
                        )}
                        <button
                            onClick={() => void refetch()}
                            disabled={isFetching}
                            className="rounded-lg border border-slate-700 px-3 py-1.5 text-slate-300 hover:bg-slate-800 hover:text-white transition-colors disabled:opacity-40"
                        >
                            {isFetching ? 'Refreshing…' : 'Refresh'}
                        </button>
                    </div>
                </div>
            </header>

            <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
                {isLoading && (
                    <div className="flex items-center justify-center h-64">
                        <div className="text-slate-500 text-sm animate-pulse">Loading secrets…</div>
                    </div>
                )}

                {isError && (
                    <div className="rounded-xl border border-red-800 bg-red-950/50 p-6 text-red-300 text-sm">
                        Failed to load secrets:{' '}
                        {error instanceof Error ? error.message : 'Unknown error'}
                    </div>
                )}

                {data && (
                    <>
                        {/* Summary chips */}
                        {(expiredCount > 0 || criticalCount > 0 || warningCount > 0) && (
                            <div className="flex items-center gap-3 mb-6 flex-wrap">
                                {expiredCount > 0 && (
                                    <span className="flex items-center gap-1.5 text-xs font-semibold text-red-400 bg-red-500/10 ring-1 ring-red-500/30 rounded-full px-3 py-1">
                                        <span className="h-1.5 w-1.5 rounded-full bg-red-400 animate-pulse" />
                                        {expiredCount} expired
                                    </span>
                                )}
                                {criticalCount > 0 && (
                                    <span className="flex items-center gap-1.5 text-xs font-semibold text-red-400 bg-red-500/10 ring-1 ring-red-500/30 rounded-full px-3 py-1">
                                        <span className="h-1.5 w-1.5 rounded-full bg-red-400" />
                                        {criticalCount} expiring within 7 days
                                    </span>
                                )}
                                {warningCount > 0 && (
                                    <span className="flex items-center gap-1.5 text-xs font-semibold text-amber-400 bg-amber-500/10 ring-1 ring-amber-500/30 rounded-full px-3 py-1">
                                        <span className="h-1.5 w-1.5 rounded-full bg-amber-400" />
                                        {warningCount} expiring within 30 days
                                    </span>
                                )}
                            </div>
                        )}

                        {/* Secrets table */}
                        <div className="rounded-xl border border-slate-800 overflow-hidden">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="bg-slate-900 border-b border-slate-800">
                                        <th className="py-2.5 px-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Secret</th>
                                        <th className="py-2.5 px-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Status</th>
                                        <th className="py-2.5 px-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Expires</th>
                                        <th className="py-2.5 px-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Last Updated</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-800">
                                    {data.secrets.map(secret => (
                                        <tr
                                            key={secret.name}
                                            className={`transition-colors ${!secret.enabled ? 'opacity-50' : ''}`}
                                        >
                                            <td className="py-2.5 px-4 font-mono text-sm text-slate-300">{secret.name}</td>
                                            <td className="py-2.5 px-4">
                                                <ExpiryBadge secret={secret} />
                                            </td>
                                            <td className="py-2.5 px-4 text-xs text-slate-400">{formatDate(secret.expiresOn)}</td>
                                            <td className="py-2.5 px-4 text-xs text-slate-500">{formatDate(secret.updatedOn)}</td>
                                        </tr>
                                    ))}
                                    {data.secrets.length === 0 && (
                                        <tr>
                                            <td colSpan={4} className="py-8 text-center text-slate-500 text-sm">
                                                No secrets found in Key Vault.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
            </main>
        </>
    )
}
